import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Link, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { CompanyAddon, getCompanyAddons } from '@services/companyAddons';
import { getSelectedFacilityFromSession } from '@utils/facilityUtils';

function addonStateRank(addon: CompanyAddon): number {
  if (addon.subscription_state === 'active') return 0;
  if (addon.subscription_state === 'coming_soon') return 2;
  return 1;
}

function addonMeta(addon: CompanyAddon): string {
  if (addon.subscription_state === 'active') {
    const usage = addon.usage ? `${addon.usage.used}/${addon.usage.total} used` : 'Usage unavailable';
    const pending = Number(addon.pending_request_count || 0);
    return `${addon.current_tier_name || 'Active'} · ${usage}${pending > 0 ? ` · ${pending} requests` : ''}`;
  }
  if (addon.subscription_state === 'coming_soon') return 'Coming soon';
  if (addon.starting_price !== null && addon.starting_price !== undefined) {
    return `Starts at $${addon.starting_price}/yr`;
  }
  return 'Not activated · Learn more';
}

export default function AddonsList() {
  const navigate = useNavigate();
  const [addons, setAddons] = useState<CompanyAddon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const facility = getSelectedFacilityFromSession<{ id?: number; facility_id?: number }>();
    const facilityId = facility?.id ?? facility?.facility_id;
    getCompanyAddons(facilityId)
      .then((items) => setAddons([...items].sort((a, b) => addonStateRank(a) - addonStateRank(b))))
      .catch((reason: unknown) => {
        setError(reason instanceof Error ? reason.message : 'Unable to load add-ons');
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <CircularProgress aria-label="Loading add-ons" size={28} />;
  }
  if (error) {
    return <Typography color="error">{error}</Typography>;
  }
  if (!addons.length) {
    return (
      <Typography>
        No add-ons available for your account.{' '}
        <Link href="mailto:contact@unicomply.ai">Contact us</Link>
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      {addons.map((addon) => {
        const comingSoon = addon.subscription_state === 'coming_soon';
        const content = (
          <Box
            data-testid={comingSoon ? 'addon-coming-soon-state' : undefined}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              px: 2,
              py: 1.5,
              border: '1px solid #d8e8ec',
              borderRadius: 1,
              '&:hover': comingSoon ? undefined : { backgroundColor: '#f4fbfc' },
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 600, color: '#065b70' }}>{addon.name}</Typography>
              {comingSoon && addon.description ? (
                <Typography variant="body2" color="text.secondary">
                  {addon.description}
                </Typography>
              ) : null}
            </Box>
                <Typography variant="body2" color="text.secondary" aria-label={comingSoon ? 'Coming soon' : undefined}>
              {addonMeta(addon)}
            </Typography>
          </Box>
        );
        return comingSoon ? (
          <Box key={addon.id}>{content}</Box>
        ) : (
          <Button
            key={addon.id}
            onClick={() => navigate(`/dashboard/billing?tab=addons&addon=${encodeURIComponent(String(addon.id))}`)}
            sx={{ display: 'block', p: 0, textAlign: 'left', textTransform: 'none' }}
          >
            {content}
          </Button>
        );
      })}
    </Box>
  );
}
